import {LitElement, html, css} from 'lit';
import {property} from 'lit/decorators/property.js';
import {customElement} from 'lit/decorators/custom-element.js';
import {connect} from 'pwa-helpers';
import {classMap} from 'lit/directives/class-map.js';
import {map} from 'lit/directives/map.js';
import {when} from 'lit/directives/when.js';
import {range} from 'lit/directives/range.js';
import {msg} from '@lit/localize';
import {
	AttributeType,
	ChoiceValueDecisionState,
} from '@viamedici-spc/configurator-ts';
import store from '../../store';
import {
	setIomoColourTemperature,
	setIomoHeadAngle,
	setIomoHeadCap,
	setIomoHeadColourTemperature,
	setIomoHeadHeight,
	setIomoHeadHousingSize,
	setIomoHeadLightDistribution, setIomoHeadModifier, setIomoHeadPitch,
	setIomoHeadsData,
	setIomoHousingSize, setIomoLightDistribution, setIomoModifier, setIomoReflectorColour, setIomoCap,
	setSelectedHead, setIomoHeadRoll
} from '../iomoSlice';
import {
	generateIomoHeadAngleSelector,
	generateIomoHeadColourTemperatureSelector,
	generateIomoHeadHeightSelector,
	generateIomoHeadHousingSizeSelector,
	generateIomoHeadIsDifferentFromBase,
	generateIomoHeadLightDistributionSelector, generateIomoHeadModifierSelector,
	generateIomoHeadPitchSelector, generateIomoHeadCapSelector,
	generateIomoHeadTypeSelector,
	selectHeadTableVisible, selectIomoAccessories, selectIomoAccessoriesInclCustom, selectIomoBracket,
	selectIomoColourTemperatureInclCustom,
	selectIomoDesignIsPole,
	selectIomoHeads,
	selectIomoHousingSizeInclCustom,
	selectIomoLightDistributionInclCustom, selectIomoModifierInclCustom,
	selectIomoNumberOfHeads,
	selectIomoOffsetFromBottom,
	selectIomoOffsetFromTop,
	selectIomoPoleHeight, selectIomoReflectorColour, selectIomoReflectorColourInclCustom,
	selectIomoSelectedHead, selectIomoCapInclCustom, generateIomoHeadRollSelector
} from '../iomoSelectors';
import {
	iomoColourTemperatureOptions,
	iomoHousingSizeOptions, iomoLightDistributionOptions, iomoModifierOptions, iomoReflectorColourOptions, iomoCapOptions
} from '../iomoModel';
import {selectSpcSession, spcValueSelectorFactory} from '../../selectors';
import {stringToAttributeId} from '../../components/spc/util/spcUtils';
import {spcExpliciteChoiceDecision} from '../../util/values';

@customElement('iomo-head-tabs')
export class IomoHeadTabs extends connect(store)(LitElement) {

	@property({type: Object})
	title = {
		de: '[Titel]',
		en: '[Title]',
		fr: '[Title]'
	};

	static styles = [
		css`
			:host {
				width: var(--assistant-widget-width);
				display: flex;
				flex-direction: column;
				gap: 10px;
			}

			.tabs {
				display: flex;
				flex-direction: row;
				padding: 10px 0 10px;
				gap: 10px;
				justify-content: left;
				align-items: center;
			}

			.title {
				font: var(--font-widget-title);
				color: var(--color-widget-text);
				margin-bottom: 12px;
			}

			.info {
				font: var(--font-widget-label);
				color: var(--color-widget-text);
			}

			.box {
				background-color: var(--color-widget-background);
				padding: 12px;
				display: flex;
				break-inside: avoid;
				flex-flow: column;
				place-content: flex-start;
				gap: var(--large-space);
			}

			button {
				width: 24px;
				height: 24px;
				border-radius: 50%;
				padding: 0;
				font: var(--font-widget-label);
				color: var(--color-widget-border);
				background-color: transparent;
				border: 1px solid var(--color-widget-border, white);
				cursor: pointer;
				display: flex;
				align-items: center;
				justify-content: center;
				transition: color 0.4s ease, background-color 0.4s ease;
			}

			button:hover {
				background-color: var(--color-widget-background);
			}

			button.collision {
				border-color: var(--color-warn);
				color: var(--color-warn);
			}

			button.selected {
				width: 40px;
				height: 40px;
				color: var(--color-black);
				background-color: var(--color-white);
			}

			button.different {
				color: var(--color-black);
				background-color: var(--color-widget-border);
			}

			button.selected.collision {
				background-color: var(--color-warn);
				border-color: var(--color-warn);
			}

			button.selected.different {
				color: var(--color-black);
				background-color: var(--color-white);
			}

			.box {
				background-color: var(--color-widget-background);
				padding: 12px;
			}

			.settings {
				flex-direction: column;
				gap: 8px;
				display: none;
			}

			.settings.active {
				display: flex;
			}

		`];

	customOption = {
		id: 'custom',
		label: {
			de: 'Mixed',
			en: 'mixed',
			fr: 'mixed',
		},
		description: {
			de: '',
			en: '',
			fr: '',
		}
	};

	constructor() {
		super();
		store.subscribe(() => this.requestUpdate());
	}

	async connectedCallback() {
		super.connectedCallback();
		addEventListener('keydown', this.handleKeydown);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		removeEventListener('keydown', this.handleKeydown);
	}

	stateChanged(state) {
		this.state = state;
		this.session = selectSpcSession(state);
		this.numberOfHeads = selectIomoNumberOfHeads(state);
		this.selectedHead = selectIomoSelectedHead(state);
		this.heads = selectIomoHeads(state);
		this.maxHeadHeight = selectIomoPoleHeight(state) - selectIomoOffsetFromTop(state);
		this.minHeadHeight = selectIomoOffsetFromBottom(state);
	}

	handleKeydown = (e) => {
		if (selectHeadTableVisible(this.state)) return; // do not use key commands when head table is active

		switch (e.keyCode) {
			case 38: // up
				this.incHeadHeight(this.selectedHead, e.shiftKey ? 0.1 : 0.01);
				break;
			case 40: // down
				this.incHeadHeight(this.selectedHead, e.shiftKey ? -0.1 : -0.01);
				break;
			case 39: // right
				this.incHeadAngle(this.selectedHead, e.shiftKey ? 10 : 1);
				break;
			case 37: // left
				this.incHeadAngle(this.selectedHead, e.shiftKey ? -10 : -1);
				break;
			case 48: // 0
				this.setSelected(null);
				break;
			case 49: // 1
				this.setSelected(0);
				break;
			case 50: // 2
				this.setSelected(1);
				break;
			case 51: // 3
				this.setSelected(2);
				break;
			case 52: // 4
				this.setSelected(3);
				break;
			case 53: // 5
				this.setSelected(4);
				break;
			case 54: // 6
				this.setSelected(5);
				break;
			case 55: // 7
				this.setSelected(6);
				break;
			case 56: // 8
				this.setSelected(7);
				break;
		}
	};

	incHeadHeight(instance, inc) {
		if (this.selectedHead === null) return;
		const selectHeight = generateIomoHeadHeightSelector(instance);
		const value = Math.min(Math.max(selectHeight(this.state) + inc, this.minHeadHeight), this.maxHeadHeight);
		store.dispatch(setIomoHeadHeight({instance: instance, value: value}));
	}

	incHeadAngle(instance, inc) {
		if (this.selectedHead === null) return;
		const selectAngle = generateIomoHeadAngleSelector(instance);
		const value = (selectAngle(this.state) + inc) % 360;
		store.dispatch(setIomoHeadAngle({instance: instance, value: value}));
	}

	hasCollisions(i) {
		return this.heads && this.heads[i]?.collisions?.length > 0;
	}

	setSelected(i) {
		store.dispatch(setSelectedHead({selectedHead: i}));
	}

	housingSizeCanBeSelected(state, id) {
		return (id !== 'custom');
			// &&
			// !(selectIomoDesignIsPole(state) && (selectIomoBracket(state) === '250' || selectIomoBracket(state) === '195') && id === '160');
			// !(selectIomoDesignIsPole(state) && (selectIomoBracket(state) === '250' || selectIomoBracket(state) === '195') && id === '160');
	}
	reflectorColourCanBeSelected(state, id) {
		return (id !== 'custom');
	}

	typeCanBeSelected(state, id, housingSize) {
		return (
			housingSize === '250' && (id === 'luminaire') ||
			housingSize === '195' && (id === 'luminaire') ||
			housingSize === '160' && (id === 'luminaire')
		);
	}

	colourTemperatureCanBeSelected(state, id) {
		return (id !== 'custom')
			&&
			!(selectIomoReflectorColour(state) === 'Gold' && id === '3000K') &&
			!(selectIomoReflectorColour(state) === 'Gold' && id === '4000K');
	}

	lightDistributionCanBeSelected(state, id) {
		return (id !== 'custom');
	}
	modifierCanBeSelected(state, id) {
		return (id !== 'custom');
	}
	capCanBeSelected(state, id) {
		return (id !== 'custom');
	}

	accessoriesCanBeSelected(state, id) {
		return (id !== 'custom');
	}

	allHeadDecision(valueId, spcChoice, value) {
		// console.log('allHeadDecision', valueId, spcChoice, value);
		// set for all heads
		if (valueId === 'root::LE_ColourTemperature_1') {
			const heads = Array.from({length: 8}, (_, i) => (value));
			store.dispatch(setIomoHeadsData({heads: heads}));

			if (value.colourTemperature === 'RGBW') {
				// make sure RGBW can be set
				const headAttributes = [
					{id: 'root::LE_ColourTemperature_1', value: value.colourTemperature},
					{id: 'root::LE_LightDistribution_1', value: 'rotationalSymmetricMediumRGBW'},
					{id: 'root::LE_ReflectorColour_1', value: 'Silver'},
					{id: 'root::LE_Accessories_1', value: 'None'},
				];
				store.dispatch(setIomoHeadsData({heads:  Array.from({length: 8}, (_, i) => ({lightDistribution: 'rotationalSymmetricMediumRGBW', accessories: 'None', reflectorColour: 'Silver'}))}));
				const spcAttributes = headAttributes.map((attribute) => spcExpliciteChoiceDecision(attribute.id, attribute.value)) ?? [];
				this.session.makeManyDecisions(spcAttributes, {type: 'KeepExistingDecisions'});
			} else {
				// make sure other can be set
				const headAttributes = [
					{id: 'root::LE_ColourTemperature_1', value: value.colourTemperature},
					{id: 'root::LE_LightDistribution_1', value: 'rotationalSymmetricMedium40'},
				];
				store.dispatch(setIomoHeadsData({heads:  Array.from({length: 8}, (_, i) => ({lightDistribution: 'rotationalSymmetricMedium40'}))}));
				const spcAttributes = headAttributes.map((attribute) => spcExpliciteChoiceDecision(attribute.id, attribute.value)) ?? [];
				this.session.makeManyDecisions(spcAttributes, {type: 'KeepExistingDecisions'});
			}
		} else {
			const heads = Array.from({length: 8}, (_, i) => (value));
			store.dispatch(setIomoHeadsData({heads: heads}));

			// update default spc
			this.session.makeDecision({
				type: AttributeType.Choice, attributeId: stringToAttributeId(valueId),
				choiceValueId: spcChoice, state: ChoiceValueDecisionState.Included
			});
		}
	}

	setAllHousingSizes(value) {
		const heads = Array.from({length: 8}, (_, i) => ({housingSize: value}));
		store.dispatch(setIomoHeadsData({heads: heads})); // all Heads
		store.dispatch(setIomoHousingSize({id: value})); // general
	}
	setAllReflectorColours(value) {
		const heads = Array.from({length: 8}, (_, i) => ({reflectorColour: value}));
		store.dispatch(setIomoHeadsData({heads: heads})); // all Heads
		store.dispatch(setIomoReflectorColour({id: value})); // general
	}
	setAllLightDistributions(value) {
		const heads = Array.from({length: 8}, (_, i) => ({lightDistribution: value}));
		store.dispatch(setIomoHeadsData({heads: heads})); // all Heads
		store.dispatch(setIomoLightDistribution({id: value})); // general
	}
	setAllColourTemperatures(value) {
		const heads = Array.from({length: 8}, (_, i) => ({colourTemperature: value}));
		store.dispatch(setIomoHeadsData({heads: heads})); // all Heads
		store.dispatch(setIomoColourTemperature({id: value})); // general
	}

	setAllModifiers(value) {
		const heads = Array.from({length: 8}, (_, i) => ({modifier: value}));
		store.dispatch(setIomoHeadsData({heads: heads})); // all Heads
		store.dispatch(setIomoModifier({id: value})); // general
	}
	setAllCaps(value) {
		const heads = Array.from({length: 8}, (_, i) => ({cap: value}));
		store.dispatch(setIomoHeadsData({heads: heads})); // all Heads
		store.dispatch(setIomoCap({id: value})); // general
	}

	getAllHeadSettings() {
		return html`
			<div class="settings active">
				<div class="box">
					${when(!selectIomoNumberOfHeads(this.state) > 1, () => html`
						<div class="info">
							${msg('This settings will be applied to all heads.')}
						</div>
					`)}
					<list-select
						.title=${{de: 'Kopfgröße', en: 'head size', fr: 'taille de la tête'}}
						.options=${iomoHousingSizeOptions.concat(this.customOption)}
						.callback=${(value) => this.setAllHousingSizes(value)}
						.selected=${selectIomoHousingSizeInclCustom}
						.canBeSelected=${this.housingSizeCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>

					<list-select
						.title=${{de: 'Reflektorfarbe', en: 'Reflector Colour', fr: 'couleur réflecteur'}}
						.options=${iomoReflectorColourOptions.concat(this.customOption)}
						.callback=${(value) => this.setAllReflectorColours(value)}
						.selected=${selectIomoReflectorColourInclCustom}
						.canBeSelected=${this.reflectorColourCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>

					<list-select
						.title=${{
							de: 'Lichtfarbe',
							en: 'Light color',
							fr: 'température de couleur'
						}}
						.options=${iomoColourTemperatureOptions.concat(this.customOption)}
						.callback=${(value) => this.setAllColourTemperatures(value)}
						.selected=${selectIomoColourTemperatureInclCustom}
						.canBeSelected=${this.colourTemperatureCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>

					<list-select
						.title=${{
							de: 'Abstrahlcharakteristik',
							en: 'light distribution',
							fr: 'distribution photométrique'
						}}
						.options=${iomoLightDistributionOptions.concat(this.customOption)}
						.callback=${(value) => this.setAllLightDistributions(value)}
						.selected=${selectIomoLightDistributionInclCustom}
						.canBeSelected=${this.lightDistributionCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>

					<list-select
						.title=${{
							de: 'Lichtmodifikation',
							en: 'light modifier',
							fr: 'light modifier'
						}}
						.options=${iomoModifierOptions.concat(this.customOption)}
						.callback=${(value) => this.setAllModifiers(value)}
						.selected=${selectIomoModifierInclCustom}
						.canBeSelected=${this.modifierCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>
					<list-select
						.title=${{
							de: 'Schute',
							en: 'cap',
							fr: 'cap'
						}}
						.options=${iomoCapOptions.concat(this.customOption)}
						.callback=${(value) => this.setAllCaps(value)}
						.selected=${selectIomoCapInclCustom}
						.canBeSelected=${this.capCanBeSelected}
						.isSatisfied=${() => true}>
					</list-select>

					<spc-attribute .attributeId=${{localId: 'LE_DimmingLevel_1'}} .hideDisabled=${true}></spc-attribute>
				</div>
			</div>
		`;
	}

	getHeadSettings(instance) {
		const selectHeight = generateIomoHeadHeightSelector(instance);
		const selectAngle = generateIomoHeadAngleSelector(instance);
		const selectPitch = generateIomoHeadPitchSelector(instance);
		const selectRoll = generateIomoHeadRollSelector(instance);

		const selectHousingSize = generateIomoHeadHousingSizeSelector(instance);
		const selectColourTemperature = generateIomoHeadColourTemperatureSelector(instance);
		const selectLightDistribution = generateIomoHeadLightDistributionSelector(instance);
		const selectModifier = generateIomoHeadModifierSelector(instance);
		const selectCap = generateIomoHeadCapSelector(instance);
		const selectType = generateIomoHeadTypeSelector(instance);

		return html`
			<div class="settings ${classMap({active: this.selectedHead === instance})}">
				<div class="box">
					<numeric-range
						min="${this.minHeadHeight}" max="${this.maxHeadHeight}" step="0.1" digits="2"
						unit="m"
						.title=${{de: 'Höhe', en: 'Height', fr: 'hauteurs'}}
						.callback=${(value) => store.dispatch(setIomoHeadHeight({instance: instance, value: value}))}
						.selected=${selectHeight}
						.isSatisfied=${() => true}>
					</numeric-range>
					<numeric-range
						max="360" min="0" step="1" digits="0"
						unit="°"
						.title=${{de: 'Winkel', en: 'Angle', fr: 'Angle'}}
						.callback=${(value) => store.dispatch(setIomoHeadAngle({instance: instance, value: value}))}
						.selected=${selectAngle}
						.isSatisfied=${() => true}>
					</numeric-range>
					<numeric-range
						max="180" min="0" step="1" digits="0"
						unit="°"
						.title=${{de: 'Pitch', en: 'Neigung', fr: 'Pitch'}}
						.callback=${(value) => store.dispatch(setIomoHeadPitch({instance: instance, value: value}))}
						.selected=${selectPitch}
						.isSatisfied=${() => true}>
					</numeric-range>
					<numeric-range
						max="360" min="0" step="1" digits="0"
						unit="°"
						.title=${{de: 'Roll', en: 'Roll', fr: 'Roll'}}
						.callback=${(value) => store.dispatch(setIomoHeadRoll({instance: instance, value: value}))}
						.selected=${selectRoll}
						.isSatisfied=${() => true}>
					</numeric-range>
				</div>

				${when(selectType(this.state) === 'luminaire', () => html`
					<div class="box">
						<list-select
							.title=${{
								de: 'Kopfgröße',
								en: 'head size',
								fr: 'taille de la tête'
							}}
							.options=${iomoHousingSizeOptions}
							.callback=${(value) => store.dispatch(setIomoHeadHousingSize({instance: instance, value: value}))}
							.selected=${selectHousingSize}
							.canBeSelected=${this.housingSizeCanBeSelected}
							.isSatisfied=${() => true}>
						</list-select>
						<list-select
							.title=${{
								de: 'Lichtfarbe',
								en: 'Light color',
								fr: 'température de couleur'
							}}
							.options=${iomoColourTemperatureOptions}
							.callback=${(value) => store.dispatch(setIomoHeadColourTemperature({instance: instance, value: value}))}
							.selected=${selectColourTemperature}
							.canBeSelected=${() => true}
							.isSatisfied=${() => true}>
						</list-select>
						<list-select
							.title=${{
								de: 'Abstrahlcharakteristik',
								en: 'Light distribution',
								fr: 'distribution photométrique'
							}}
							.options=${iomoLightDistributionOptions}
							.callback=${(value) => store.dispatch(setIomoHeadLightDistribution({instance: instance, value: value}))}
							.selected=${selectLightDistribution}
							.canBeSelected=${() => true}
							.isSatisfied=${() => true}>
						</list-select>
						<list-select
							.title=${{
								de: 'Lichtmodifikation',
								en: 'light modifier',
								fr: 'light modifier'
							}}
							.options=${iomoModifierOptions}
							.callback=${(value) => store.dispatch(setIomoHeadModifier({instance: instance, value: value}))}
							.selected=${selectModifier}
							.canBeSelected=${() => true}
							.isSatisfied=${() => true}>
						</list-select>
						<list-select
							.title=${{
								de: 'Schute',
								en: 'cap',
								fr: 'cap'
							}}
							.options=${iomoCapOptions}
							.callback=${(value) => store.dispatch(setIomoHeadCap({instance: instance, value: value}))}
							.selected=${selectCap}
							.canBeSelected=${() => true}
							.isSatisfied=${() => true}>
						</list-select>
					</div>
				`)}

			</div>
		`;
	}

	render() {
		return html`
			${when(this.numberOfHeads > 1, () => html`
				<div class="box">
					<div>
						<div class="title">${msg('Selected Head')}</div>
						<div class="tabs">
							<button class="${classMap({selected: this.selectedHead === null})}" @click="${() => this.setSelected(null)}">${msg('all')}
							</button>
							${map(range(this.numberOfHeads), (i) => html`
								<button class="${classMap({
									selected: this.selectedHead === i,
									different: generateIomoHeadIsDifferentFromBase(i)(this.state),
									collision: this.hasCollisions(i)
								})}"
										@click="${() => this.setSelected(i)}">${i + 1}
								</button>`)}
						</div>
					</div>
				</div>
			`)}
			${when(this.selectedHead === null || this.numberOfHeads === 1,
				() => this.getAllHeadSettings(),
				() => map(range(8), (i) => this.getHeadSettings(i)))}
		`;
	}
}
